;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { buildQueryParams } from '@workspace/ui/lib';
import type { ApiResponse } from '@workspace/ui/types';
import fetchAPI from '@/lib/fetch-api';
import type {
  ConfirmPaydayExtensionPayload,
  EligiblePaydayCountry,
  PaydayExtension,
  PaydayExtensionOption,
  SalaryAchievements,
  SalaryActivityFilters,
  SalaryDetails,
  SalaryDistribution,
  EstimatedNetSalary,
  SalaryProjection,
  SalaryRewardCalculation,
  SalaryRewardSummary,
  SalarySummary,
  SalaryTransaction,
  UpdateSalaryDistributionPayload,
} from '@/types/salary-rewards';

function billingUrl(path: string): string {
  return `${process.env.NEXT_PUBLIC_BILLING_API_URL}${path}`;
}

function assertSuccess<T>(response: ApiResponse<T>, failureMessage: string): T {
  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.message || response.error || failureMessage);
  }

  if (response.data === undefined || response.data === null) {
    throw new Error(response.message || failureMessage);
  }

  return response.data;
}

function assertNullableSuccess<T>(
  response: ApiResponse<T | null>,
  failureMessage: string
): T | null {
  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.message || response.error || failureMessage);
  }

  return response.data ?? null;
}

export async function getEligiblePaydayCountries(): Promise<ApiResponse<EligiblePaydayCountry[]>> {
  const response = await fetchAPI<EligiblePaydayCountry[]>(
    billingUrl('/paydays/countries/eligible'),
    {
      method: 'GET',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(
      response.message || response.error || 'Failed to fetch eligible payday countries'
    );
  }

  return {
    ...response,
    message: response.message || 'Eligible payday countries fetched successfully',
  };
}

export async function getEstimatedNetSalary(): Promise<EstimatedNetSalary> {
  const response = await fetchAPI<EstimatedNetSalary>(billingUrl('/estimated-net-salaries'), {
    method: 'GET',
  });

  return assertSuccess(response, 'Failed to fetch estimated net salary');
}

export async function getSalaryRewardSummary(): Promise<SalaryRewardSummary> {
  const response = await fetchAPI<SalaryRewardSummary>(billingUrl('/salary-rewards/summary'), {
    method: 'GET',
  });

  return assertSuccess(response, 'Failed to fetch salary reward summary');
}

export async function getSalaryDistribution(): Promise<SalaryDistribution> {
  const response = await fetchAPI<SalaryDistribution>(billingUrl('/salary-rewards/distribution'), {
    method: 'GET',
  });

  return assertSuccess(response, 'Failed to fetch salary distribution');
}

export async function updateSalaryDistribution(
  payload: UpdateSalaryDistributionPayload
): Promise<SalaryDistribution> {
  const response = await fetchAPI<SalaryDistribution>(billingUrl('/salary-rewards/distribution'), {
    method: 'PUT',
    headers: { 'Idempotency-Key': crypto.randomUUID() },
    body: JSON.stringify(payload),
  });

  return assertSuccess(response, 'Failed to update salary distribution');
}

export async function calculateSalaryReward(
  selectedPaydate: string
): Promise<SalaryRewardCalculation> {
  const response = await fetchAPI<SalaryRewardCalculation>(
    billingUrl('/salary-rewards/calculate'),
    {
      method: 'POST',
      body: JSON.stringify({ selected_paydate: selectedPaydate }),
    }
  );

  return assertSuccess(response, 'Failed to calculate salary reward');
}

export async function getPaydayExtensionOptions(): Promise<PaydayExtensionOption[]> {
  const response = await fetchAPI<PaydayExtensionOption[]>(
    billingUrl('/salary-rewards/payday-extension/options'),
    { method: 'GET' }
  );

  return assertSuccess(response, 'Failed to fetch payday extension options');
}

export async function getCurrentPaydayExtension(): Promise<PaydayExtension | null> {
  const response = await fetchAPI<PaydayExtension | null>(
    billingUrl('/salary-rewards/payday-extension/current'),
    { method: 'GET' }
  );

  return assertNullableSuccess(response, 'Failed to fetch current payday extension');
}

export async function confirmPaydayExtension(
  payload: ConfirmPaydayExtensionPayload
): Promise<PaydayExtension> {
  const response = await fetchAPI<PaydayExtension>(
    billingUrl('/salary-rewards/payday-extension/confirm'),
    {
      method: 'POST',
      headers: { 'Idempotency-Key': crypto.randomUUID() },
      body: JSON.stringify(payload),
    }
  );

  return assertSuccess(response, 'Failed to confirm payday extension');
}

export async function getSalaryDetails(period: string): Promise<SalaryDetails | null> {
  const response = await fetchAPI<SalaryDetails | null>(
    billingUrl(`/salary-rewards/salary-details?period=${encodeURIComponent(period)}`),
    { method: 'GET' }
  );

  return assertNullableSuccess(response, 'Failed to fetch salary details');
}

export async function getSalarySummary(walletId: number): Promise<SalarySummary> {
  const response = await fetchAPI<SalarySummary>(`/wallets/${walletId}/salary-summary`, {
    method: 'GET',
  });

  return assertSuccess(response, 'Failed to fetch salary summary');
}

export interface SalaryActivityPage {
  items: SalaryTransaction[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export async function getSalaryActivity(
  walletId: number,
  filters: SalaryActivityFilters = {}
): Promise<SalaryActivityPage> {
  const queryParams = buildQueryParams({
    category: filters.category,
    direction: filters.direction,
    date_from: filters.date_from,
    date_to: filters.date_to,
    page: filters.page,
    per_page: filters.per_page,
    status: filters.status,
  });

  const queryString = queryParams.toString();
  const response = await fetchAPI<SalaryTransaction[]>(
    `/wallets/${walletId}/salary-activity${queryString ? `?${queryString}` : ''}`,
    { method: 'GET' }
  );

  const items = assertSuccess(response, 'Failed to fetch salary activity');
  const pagination = response.pagination;

  return {
    items,
    current_page: pagination?.current_page ?? filters.page ?? 1,
    per_page: pagination?.per_page ?? filters.per_page ?? items.length,
    total: pagination?.total ?? items.length,
    last_page: pagination?.last_page ?? 1,
  };
}

export async function getSalaryAchievements(walletId: number): Promise<SalaryAchievements> {
  const response = await fetchAPI<SalaryAchievements>(`/wallets/${walletId}/salary-achievements`, {
    method: 'GET',
  });

  return assertSuccess(response, 'Failed to fetch salary achievements');
}

export async function getSalaryProjection(
  walletId: number,
  months?: number
): Promise<SalaryProjection> {
  const response = await fetchAPI<SalaryProjection>(
    `/wallets/${walletId}/salary-projection${months ? `?months=${months}` : ''}`,
    { method: 'GET' }
  );

  return assertSuccess(response, 'Failed to fetch salary projection');
}
